import { cx, useAppVariant } from "@jugl-web/utils";
import { FC, useMemo } from "react";

const WEB_SKELETON_ROW_COUNT = 6;
const MOBILE_SKELETON_ROW_COUNT = 12;

type SkeletonTableVariant = "web" | "mobile";

type SkeletonColumnConfig = {
  id: string;
  width: number;
  headerBarWidth: number;
  cellContent: "ticket-name" | "team-notes" | "icon-text" | "assignees";
};

const WEB_SKELETON_COLUMNS: SkeletonColumnConfig[] = [
  {
    id: "ticket-name",
    width: 368,
    headerBarWidth: 130,
    cellContent: "ticket-name",
  },
  {
    id: "team-notes",
    width: 110,
    headerBarWidth: 60,
    cellContent: "team-notes",
  },
  {
    id: "customer",
    width: 180,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
  {
    id: "status",
    width: 180,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
  {
    id: "assignees",
    width: 180,
    headerBarWidth: 60,
    cellContent: "assignees",
  },
  {
    id: "priority",
    width: 200,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
  {
    id: "due-date",
    width: 200,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
  {
    id: "created-at",
    width: 200,
    headerBarWidth: 60,
    cellContent: "icon-text",
  },
];

const MOBILE_SKELETON_COLUMNS: SkeletonColumnConfig[] = [
  {
    id: "ticket-name",
    width: 180,
    headerBarWidth: 72,
    cellContent: "ticket-name",
  },
  {
    id: "team-notes",
    width: 100,
    headerBarWidth: 48,
    cellContent: "team-notes",
  },
  {
    id: "customer",
    width: 180,
    headerBarWidth: 48,
    cellContent: "icon-text",
  },
  {
    id: "status",
    width: 160,
    headerBarWidth: 48,
    cellContent: "icon-text",
  },
  {
    id: "assignees",
    width: 160,
    headerBarWidth: 48,
    cellContent: "assignees",
  },
  {
    id: "priority",
    width: 160,
    headerBarWidth: 48,
    cellContent: "icon-text",
  },
  {
    id: "due-date",
    width: 200,
    headerBarWidth: 48,
    cellContent: "icon-text",
  },
  {
    id: "created-at",
    width: 200,
    headerBarWidth: 48,
    cellContent: "icon-text",
  },
];

const SKELETON_TABLE_CONFIG: Record<
  SkeletonTableVariant,
  {
    rowCount: number;
    columns: SkeletonColumnConfig[];
    bodyCellHeightClassName: string;
    cellPaddingClassName: string;
    ticketNamePrimaryWidth: number;
    ticketNameSecondaryWidth: number;
    iconTextWidth: number;
    assigneeCount: number;
  }
> = {
  web: {
    rowCount: WEB_SKELETON_ROW_COUNT,
    columns: WEB_SKELETON_COLUMNS,
    bodyCellHeightClassName: "h-[79px]",
    cellPaddingClassName: "px-4",
    ticketNamePrimaryWidth: 135,
    ticketNameSecondaryWidth: 135,
    iconTextWidth: 80,
    assigneeCount: 5,
  },
  mobile: {
    rowCount: MOBILE_SKELETON_ROW_COUNT,
    columns: MOBILE_SKELETON_COLUMNS,
    bodyCellHeightClassName: "h-[74px]",
    cellPaddingClassName: "px-3",
    ticketNamePrimaryWidth: 88,
    ticketNameSecondaryWidth: 88,
    iconTextWidth: 56,
    assigneeCount: 4,
  },
};

interface SkeletonBarProps {
  width: number;
  height: number;
  variant?: "primary" | "secondary";
}

const SkeletonBar: FC<SkeletonBarProps> = ({
  width,
  height,
  variant = "secondary",
}) => (
  <div
    className={cx(
      "shrink-0 rounded-lg",
      variant === "primary" ? "bg-grey-200" : "bg-grey-100"
    )}
    style={{ width, height }}
  />
);

const SkeletonCircle: FC = () => (
  <div className="size-4 shrink-0 rounded-full border border-solid border-white bg-grey-200" />
);

interface TicketNameCellSkeletonProps {
  primaryWidth: number;
  secondaryWidth: number;
}

const TicketNameCellSkeleton: FC<TicketNameCellSkeletonProps> = ({
  primaryWidth,
  secondaryWidth,
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center gap-1.5">
      <SkeletonCircle />
      <SkeletonBar width={primaryWidth} height={16} variant="primary" />
    </div>
    <SkeletonBar width={secondaryWidth} height={8} />
  </div>
);

interface IconTextCellSkeletonProps {
  textWidth: number;
}

const IconTextCellSkeleton: FC<IconTextCellSkeletonProps> = ({ textWidth }) => (
  <div className="flex items-center gap-1.5">
    <SkeletonCircle />
    <SkeletonBar width={textWidth} height={16} />
  </div>
);

interface AssigneesCellSkeletonProps {
  count: number;
}

const AssigneesCellSkeleton: FC<AssigneesCellSkeletonProps> = ({ count }) => (
  <div className="flex items-center">
    {Array.from({ length: count }, (_, index) => (
      <div
        key={index}
        className={cx(
          "size-4 shrink-0 rounded-full border border-solid border-white bg-grey-200",
          index < count - 1 && "-mr-1"
        )}
      />
    ))}
  </div>
);

interface SkeletonCellContentProps {
  type: SkeletonColumnConfig["cellContent"];
  tableVariant: SkeletonTableVariant;
}

const SkeletonCellContent: FC<SkeletonCellContentProps> = ({
  type,
  tableVariant,
}) => {
  const {
    ticketNamePrimaryWidth,
    ticketNameSecondaryWidth,
    iconTextWidth,
    assigneeCount,
  } = SKELETON_TABLE_CONFIG[tableVariant];

  switch (type) {
    case "ticket-name":
      return (
        <TicketNameCellSkeleton
          primaryWidth={ticketNamePrimaryWidth}
          secondaryWidth={ticketNameSecondaryWidth}
        />
      );
    case "team-notes":
      return <SkeletonCircle />;
    case "icon-text":
      return <IconTextCellSkeleton textWidth={iconTextWidth} />;
    case "assignees":
      return <AssigneesCellSkeleton count={assigneeCount} />;
    default:
      return null;
  }
};

interface SkeletonCellProps {
  column: SkeletonColumnConfig;
  variant: "header" | "body";
  tableVariant: SkeletonTableVariant;
}

const SkeletonCell: FC<SkeletonCellProps> = ({
  column,
  variant,
  tableVariant,
}) => {
  const isTeamNotes = column.cellContent === "team-notes";
  const { bodyCellHeightClassName, cellPaddingClassName } =
    SKELETON_TABLE_CONFIG[tableVariant];

  return (
    <div
      className={cx(
        "shrink-0 border-r border-solid border-dark-100 bg-white last:border-r-0",
        cellPaddingClassName,
        variant === "header" && "flex h-10 items-center",
        variant === "body" && cx("flex items-center", bodyCellHeightClassName),
        isTeamNotes && "justify-center"
      )}
      style={{ width: column.width }}
    >
      {variant === "header" ? (
        <SkeletonBar width={column.headerBarWidth} height={10} />
      ) : (
        <SkeletonCellContent
          type={column.cellContent}
          tableVariant={tableVariant}
        />
      )}
    </div>
  );
};

export const TicketsTableSkeleton: FC = () => {
  const { isMobile } = useAppVariant();

  const tableVariant: SkeletonTableVariant = isMobile ? "mobile" : "web";
  const { rowCount, columns } = useMemo(
    () => SKELETON_TABLE_CONFIG[tableVariant],
    [tableVariant]
  );

  return (
    <div
      aria-busy
      aria-label="Loading tickets"
      className="w-fit min-w-full animate-pulse border-b border-solid border-[#EEF2F5]"
    >
      <div className="flex">
        {columns.map((column) => (
          <SkeletonCell
            key={column.id}
            column={column}
            variant="header"
            tableVariant={tableVariant}
          />
        ))}
      </div>

      {Array.from({ length: rowCount }, (_, rowIndex) => (
        <div
          key={rowIndex}
          className="flex border-t border-solid border-[#EEF2F5]"
        >
          {columns.map((column) => (
            <SkeletonCell
              key={column.id}
              column={column}
              variant="body"
              tableVariant={tableVariant}
            />
          ))}
        </div>
      ))}
    </div>
  );
};
